import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import styled from 'styled-components';
import { useDetachedState } from '@/components/hooks/use-detached-state';
import { OverflowLeft, OverflowRight } from '@/components/overflow';
import { HSpace } from '@/components/spacing';

export interface HorizontalSliderProps {
  id?: string;

  arrowsTop?: number;
  arrowsLeft?: number;
  arrowsRight?: number;

  startOffset?: number;
  endOffset?: number;

  extraStartPadding?: number;
  extraEndPadding?: number;
  showButtonsOnHover?: boolean;

  sizeLeftChevron?: number;
  sizeRightChevron?: number;

  speed?: number;

  onClick?: () => void;

  className?: string;
  children?: React.ReactNode;
}

export const HorizontalSlider = forwardRef<
  HTMLDivElement,
  HorizontalSliderProps
>(
  (
    {
      id,
      children,
      arrowsTop,
      arrowsLeft,
      arrowsRight,
      startOffset,
      endOffset,
      extraStartPadding,
      extraEndPadding,
      showButtonsOnHover,
      onClick,
      className,
      sizeLeftChevron = 8,
      sizeRightChevron = 8,
      speed = 2,
    }: HorizontalSliderProps,
    ref,
  ) => {
    const scrollRef = useRef(null);

    const [showPrev, setShowPrev] = useState(
      scrollRef?.current?.scrollLeft === 0,
    );
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_1, setIsHoveringPrev, isHoveringPrev] = useDetachedState(false);

    const [showNext, setShowNext] = useState(
      scrollRef?.current?.scrollWidth > scrollRef?.current?.clientWidth,
    );
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_2, setIsHoveringNext, isHoveringNext] = useDetachedState(false);

    const [scrollPos, setScrollPos] = useDetachedState(
      scrollRef?.current?.scrollLeft || 0,
    );

    const [showButtons, setShowButtons] = useState(!showButtonsOnHover);

    const onScroll = (ev) => {
      if (ev?.target) {
        setScrollPos(Math.round(ev.target.scrollLeft));
      }
    };

    useEffect(() => {
      if (scrollRef?.current) {
        setTimeout(() => {
          const scroller = scrollRef?.current as HTMLDivElement;
          scroller.scrollBy({ left: 1 });
        }, 500);
      }
    }, [scrollRef?.current]);

    const onPrevHover = () => {
      setIsHoveringPrev(true);
      movePrev();
    };

    const onPrevExitHover = () => {
      setIsHoveringPrev(false);
    };

    const movePrev = () => {
      setTimeout(() => {
        if (isHoveringPrev?.current && scrollRef?.current?.scrollBy) {
          scrollRef?.current?.scrollBy(-speed, 0);
          movePrev();
        }
      }, 5);
    };

    const onNextHover = () => {
      setIsHoveringNext(true);
      moveNext();
    };

    const moveNext = () => {
      setTimeout(() => {
        if (isHoveringNext?.current && scrollRef?.current?.scrollBy) {
          scrollRef?.current?.scrollBy(speed, 0);
          moveNext();
        }
      }, 5);
    };

    const onNextExitHover = () => {
      setIsHoveringNext(false);
    };

    useEffect(() => {
      if (scrollRef?.current) {
        const endLeftover =
          scrollRef.current.scrollWidth -
          scrollRef.current.clientWidth -
          scrollPos;

        const startLeftOver = scrollRef?.current?.scrollLeft;
        if (endLeftover <= (endOffset || 0)) {
          setShowNext(false);
        } else {
          setShowNext(true);
        }

        if (startLeftOver >= (startOffset || 5)) {
          setShowPrev(true);
        } else {
          setShowPrev(false);
        }
      }
    }, [scrollRef?.current, scrollPos]);

    useEffect(() => {
      if (scrollRef?.current) {
        const scroller = scrollRef?.current as HTMLDivElement;
        scroller?.addEventListener(`scroll`, onScroll);
      }
      return () => {
        if (scrollRef?.current) {
          const scroller = scrollRef?.current as HTMLDivElement;
          scroller?.removeEventListener(`scroll`, onScroll);
        }
      };
    }, [scrollRef]);

    useEffect(() => {
      if (scrollRef?.current) {
        scrollRef.current.scrollLeft = 0;
      }
    }, [scrollRef]);

    const onWrapperMouseEnter = () => {
      if (showButtonsOnHover) {
        setShowButtons(true);
      }
    };

    const onWrapperMouseLeave = () => {
      if (showButtonsOnHover) {
        setShowButtons(false);
      }
    };

    return (
      <Wrapper
        ref={ref}
        onMouseEnter={onWrapperMouseEnter}
        onMouseLeave={onWrapperMouseLeave}
        onClick={onClick}
        className={className}
      >
        <ScrollButtonWrapper
          top={arrowsTop}
          left={arrowsLeft}
          right={arrowsRight}
          className="left"
          isShown={showPrev && showButtons}
          onMouseEnter={onPrevHover}
          onMouseLeave={onPrevExitHover}
        >
          <ScrollButton className="left scroll-button">
            <FaChevronLeft size={sizeLeftChevron} />
          </ScrollButton>
        </ScrollButtonWrapper>

        <ScrollWrapper>
          {showPrev && (
            <OverflowLeft className="overflow" left="-2rem" width="5rem" />
          )}
          <Scroller className="scroller" id={id} ref={scrollRef}>
            {extraStartPadding > 0 && (
              <HSpace className="start-space" value={extraStartPadding} />
            )}
            {children}
            {extraEndPadding > 0 && (
              <HSpace className="end-space" value={extraEndPadding} />
            )}
          </Scroller>
          {showNext && <OverflowRight className="overflow" right="-1rem" />}
        </ScrollWrapper>
        <ScrollButtonWrapper
          top={arrowsTop}
          left={arrowsLeft}
          right={arrowsRight}
          className="right"
          isShown={showNext && showButtons}
          onMouseEnter={onNextHover}
          onMouseLeave={onNextExitHover}
        >
          <ScrollButton className="right scroll-button">
            <FaChevronRight size={sizeRightChevron} />
          </ScrollButton>
        </ScrollButtonWrapper>
      </Wrapper>
    );
  },
);

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 100%;
  overflow: visible;
`;

const ScrollWrapper = styled.div`
  position: relative;
  height: auto;
  width: 100%;
  overflow: hidden;
  max-width: 100%;
`;

const Scroller = styled.div`
  max-width: 100%;
  position: relative;
  width: 100%;
  list-style: none;
  padding: 10px;
  margin: -10px;
  overflow-x: scroll;
  display: flex;
  flex-shrink: 0;
  flex-grow: 1;
  flex-direction: row;
  flex-wrap: nowrap;
  gap: 1.5rem;
  justify-content: flex-start;
  justify-items: flex-start;
  align-items: center;

  && > * {
    flex: 0 0 auto;
  }
`;

const ScrollButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;

  position: absolute;
  top: ${(p) => p.top || 0}rem;
  bottom: 0;

  &&.left {
    padding-left: ${(p) => p.left || -0.7}rem;
    left: 0;
    cursor: w-resize;
  }

  &&.right {
    padding-right: ${(p) => p.right || -0.7}rem;
    right: 0;
    cursor: e-resize;
  }

  z-index: 100;
  height: calc(100dvh - 20rem);

  :hover .scroll-button {
    box-shadow: 0 6px 7px 0 rgba(181, 193, 204, 1);
    opacity: 0.6;
    background: rgba(222, 222, 222, 0.1);
    backdrop-filter: blur(4px);
  }

  opacity: ${(p) => (p.isShown ? `1` : `0`)};
  pointer-events: ${(p) => (p.isShown ? `auto` : `none`)};
  transition: all 0.2s linear;
`;

const ScrollButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;

  &&.left {
    cursor: w-resize;
  }

  &&.right {
    cursor: e-resize;
  }

  background: white;
  border-radius: 50%;

  height: 2.5rem;
  width: 2.5rem;
  aspect-ratio: 1;

  box-shadow: 0 6px 7px 0 rgba(181, 193, 204, 0.7);
`;
